import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { FileText, Plus, Search, MoreVertical, Pencil, Trash2, Send, Clock, ExternalLink, Image as ImageIcon } from "lucide-react";

export default function ArticlesPage() {
  const { token } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await fetch(`${API}/articles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setArticles(data);
      }
    } catch (error) {
      console.error("Failed to fetch articles:", error);
      toast.error("Erreur lors du chargement des articles");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      const response = await fetch(`${API}/articles/${deleteId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success("Article supprimé");
        fetchArticles();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleteId(null);
    }
  };

  const filteredArticles = articles.filter((article) =>
    article.title.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case "published":
        return <Badge className="bg-[#20C997] hover:bg-[#20C997] rounded-lg">Publié</Badge>;
      case "scheduled":
        return <Badge className="bg-[#1FB6FF] hover:bg-[#1FB6FF] rounded-lg">Planifié</Badge>;
      case "draft":
      default:
        return <Badge className="bg-gray-200 text-[#6B7280] hover:bg-gray-200 rounded-lg">Brouillon</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="articles-loading">
        <div className="animate-pulse text-[#6B7280]">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="articles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1E2A5A]">Mes articles</h1>
          <p className="text-[#6B7280] mt-1">Gérez et publiez vos articles</p>
        </div>
        <Button className="bg-[#1E2A5A] hover:bg-[#20C997] rounded-xl transition-colors" asChild data-testid="new-article-btn">
          <Link to="/articles/new">
            <Plus className="w-4 h-4 mr-2" />
            Nouvel article
          </Link>
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
        <Input
          placeholder="Rechercher un article..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-11 h-12 rounded-xl border-gray-200 focus:border-[#20C997] focus:ring-[#20C997]"
          data-testid="search-articles-input"
        />
      </div>

      {/* Articles List */}
      {filteredArticles.length > 0 ? (
        <div className="space-y-4">
          {filteredArticles.map((article) => (
            <Card 
              key={article.id} 
              className="border-0 bg-white rounded-2xl soft-shadow hover-lift transition-all duration-200"
              data-testid={`article-card-${article.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusBadge(article.status)}
                      {article.site_ids?.length > 0 && (
                        <Badge variant="outline" className="text-xs rounded-lg border-[#1E2A5A]/20 text-[#1E2A5A]">
                          {article.site_ids.length} site{article.site_ids.length > 1 ? "s" : ""}
                        </Badge>
                      )}
                    </div>
                    <Link 
                      to={`/articles/${article.id}`}
                      className="block group"
                    >
                      <h3 className="text-lg font-semibold font-heading text-[#1E2A5A] group-hover:text-[#20C997] transition-colors">
                        {article.title || "Sans titre"}
                      </h3>
                    </Link>
                    {article.excerpt && (
                      <p className="text-[#6B7280] text-sm mt-1 line-clamp-2">
                        {article.excerpt}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-3 text-sm text-[#6B7280]">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{new Date(article.updated_at).toLocaleDateString('fr-FR', { 
                          day: 'numeric', 
                          month: 'short',
                          year: 'numeric'
                        })}</span>
                      </div>
                      {article.categories?.length > 0 && (
                        <div className="flex gap-1">
                          {article.categories.slice(0, 2).map((cat, i) => (
                            <Badge key={i} variant="outline" className="text-xs rounded-lg border-gray-200">
                              {cat}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    {article.published_to?.length > 0 && (
                      <div className="flex flex-col gap-1 mt-3 pt-3 border-t border-gray-100">
                        <span className="text-xs text-[#6B7280] flex items-center gap-1">
                          <Send className="w-3 h-3 text-[#20C997]" />
                          Publié sur :
                        </span>
                        <div className="flex flex-wrap gap-2">
                          {article.published_to.map((pub, idx) => (
                            <a
                              key={idx}
                              href={pub.wp_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-[#1E2A5A] hover:text-[#20C997] bg-[#F5F7FA] px-2 py-1 rounded-lg transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-3 h-3" />
                              {pub.site_name}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-[#6B7280] hover:text-[#1E2A5A] hover:bg-[#F5F7FA]" data-testid={`article-menu-${article.id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-xl">
                      <DropdownMenuItem asChild>
                        <Link to={`/articles/${article.id}`} className="flex items-center cursor-pointer">
                          <Pencil className="w-4 h-4 mr-2" />
                          Modifier
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => setDeleteId(article.id)}
                        className="text-red-500 focus:text-red-500 cursor-pointer"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-0 bg-white rounded-2xl soft-shadow">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-20 h-20 bg-[#F5F7FA] rounded-full flex items-center justify-center mb-4">
              <FileText className="w-10 h-10 text-[#6B7280]/50" />
            </div>
            <h3 className="text-lg font-heading font-semibold text-[#1E2A5A] mb-2">
              {search ? "Aucun résultat" : "Aucun article"}
            </h3>
            <p className="text-[#6B7280] text-center mb-6 max-w-md">
              {search 
                ? "Aucun article ne correspond à votre recherche"
                : "Créez votre premier article pour commencer à publier sur vos sites WordPress"
              }
            </p>
            {!search && (
              <Button className="bg-[#1E2A5A] hover:bg-[#20C997] rounded-xl transition-colors" asChild data-testid="empty-new-article-btn">
                <Link to="/articles/new">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un article
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#1E2A5A]">Supprimer cet article ?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#6B7280]">
              Cette action est irréversible. L'article sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 rounded-xl"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
